import { Box } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  Coverage,
  GridDomain,
  PointSeriesDomain,
} from '@opengeoweb/shared';
import { CustomIconButton } from '@opengeoweb/shared';
import { Close } from '@opengeoweb/theme';
import HeatMap from './HeatMap';
import IntegratedDensity from './IntegratedDensity';
import { coverageToRowsGrid } from '../utils/coverageToRowsGrid';
import type { Mode, FlySafeRow } from '../utils/types';

interface Props {
  id: number;
  onClose: (id: number) => void;
  mode: Mode;
  timeWindow: [Date, Date];
  mapTime?: Date | null;
  onTimeSelect?: (time: Date) => void;
  stationId?: string;
  coverage?: Coverage<GridDomain>;
  szaCoverage?: Coverage<PointSeriesDomain>;
  showVPTS: boolean;
  showVID: boolean;
}

interface DisplayState {
  rows: FlySafeRow[] | null;
}

// For each location, the plots use the timeWindow specified in the panel slider
const FlySafeLocation = React.memo((props: Props): ReactNode => {
  const {
    id,
    onClose,
    mode,
    timeWindow,
    mapTime,
    onTimeSelect,
    stationId,
    coverage,
    szaCoverage,
    showVPTS,
    showVID,
  } = props;

  // Data used by both plots (heat map and integrated density)
  // SZA is further processed in the integrated density plot
  const rowsFromCoverage = useMemo(
    () => coverageToRowsGrid(coverage),
    [coverage],
  );

  const [display, setDisplay] = useState<DisplayState>(() => ({
    rows: coverageToRowsGrid(coverage),
  }));

  const covKey = useMemo(() => {
    const tVals: string[] | undefined = coverage?.domain.axes.t?.values;
    const first = tVals?.[0] ?? '';
    const last = tVals?.[tVals.length - 1] ?? '';
    const len = tVals?.length ?? 0;
    return `${stationId ?? ''}|${len}|${first}|${last}`;
  }, [coverage, stationId]);

  const prevCovKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!coverage) {
      return;
    }
    if (prevCovKeyRef.current === covKey) {
      return;
    }
    prevCovKeyRef.current = covKey;

    setDisplay({ rows: rowsFromCoverage });
  }, [coverage, covKey, rowsFromCoverage]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          pb: 1.5,
          position: 'absolute',
          right: 0,
          top: 0,
        }}
      >
        <CustomIconButton
          aria-label={`close-${id}`}
          onClick={() => onClose(id)}
          sx={{
            ml: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Close />
        </CustomIconButton>
      </Box>

      <Box>
        {!!showVPTS && (
          <HeatMap
            mapTime={mapTime}
            mode={mode}
            onTimeSelect={onTimeSelect}
            rows={display.rows}
            stationId={stationId}
            timeWindow={timeWindow}
          />
        )}

        {!!showVID && (
          <IntegratedDensity
            forecastData={null}
            id={id}
            mapTime={mapTime}
            mode={mode}
            observedData={display.rows}
            onTimeSelect={onTimeSelect}
            preferredHeight="20vh"
            stationId={stationId}
            szaCoverage={szaCoverage}
            timeWindow={timeWindow}
          />
        )}
      </Box>
    </Box>
  );
});
export default FlySafeLocation;
